import { Bell01, XClose } from '@untitledui/icons';
import { toast } from 'sonner';

import type { WSProposalCreated } from '../realtime/types';
import { PROPOSAL_TOAST_POSITION } from './toastPosition';

const TOAST_OPTIONS = {
  duration: 5000,
  position: PROPOSAL_TOAST_POSITION,
  style: {
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
    padding: 0,
  },
};

/**
 * Show a toast for a single newly-arrived proposal. The entire card surface
 * is clickable and navigates to /{orgId}/actions/{proposalId}. The XClose
 * button in the corner dismisses without navigating.
 */
export function showNewProposalToast(
  proposal: WSProposalCreated,
  orgId: number
): void {
  toast.custom(t => {
    const handleView = () => {
      toast.dismiss(t);
      window.location.assign(`/${orgId}/actions/${proposal.id}`);
    };

    const handleDismiss = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      toast.dismiss(t);
    };

    return (
      <div
        role='status'
        aria-live='polite'
        aria-atomic='true'
        className='relative min-w-[320px] max-w-[420px]'
      >
        <button
          type='button'
          onClick={handleView}
          className='flex items-start gap-3 p-3.5 pr-9 w-full text-left bg-bg-primary border border-border-secondary rounded-xl shadow-lg cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600'
        >
          <div className='flex items-center justify-center size-9 rounded-full bg-secondary shrink-0 mt-0.5'>
            <Bell01 className='size-5 text-fg-quaternary' aria-hidden='true' />
          </div>
          <div className='flex-1 min-w-0 space-y-1'>
            <p className='text-sm font-semibold text-fg-primary'>New action</p>
            <p className='text-sm text-fg-secondary truncate'>
              {proposal.title?.trim() || 'Untitled action'}
            </p>
          </div>
        </button>
        <button
          type='button'
          onClick={handleDismiss}
          aria-label='Dismiss'
          className='absolute top-2 right-2 text-fg-tertiary hover:text-fg-secondary transition-colors p-1 rounded-md hover:bg-base-300'
        >
          <XClose className='size-4' />
        </button>
      </div>
    );
  }, TOAST_OPTIONS);
}

/**
 * Show a summary toast when 2+ proposals arrive in a short window.
 * The entire card navigates to /{orgId}/actions (list view).
 */
export function showNewProposalsSummaryToast(
  count: number,
  orgId: number
): void {
  toast.custom(t => {
    const handleView = () => {
      toast.dismiss(t);
      window.location.assign(`/${orgId}/actions`);
    };

    const handleDismiss = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      toast.dismiss(t);
    };

    return (
      <div
        role='status'
        aria-live='polite'
        aria-atomic='true'
        className='relative min-w-[320px] max-w-[420px]'
      >
        <button
          type='button'
          onClick={handleView}
          className='flex items-start gap-3 p-3.5 pr-9 w-full text-left bg-bg-primary border border-border-secondary rounded-xl shadow-lg cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600'
        >
          <div className='flex items-center justify-center size-9 rounded-full bg-secondary shrink-0 mt-0.5'>
            <Bell01 className='size-5 text-fg-quaternary' aria-hidden='true' />
          </div>
          <div className='flex-1 min-w-0 space-y-1'>
            <p className='text-sm font-semibold text-fg-primary'>{`${count} new actions`}</p>
          </div>
        </button>
        <button
          type='button'
          onClick={handleDismiss}
          aria-label='Dismiss'
          className='absolute top-2 right-2 text-fg-tertiary hover:text-fg-secondary transition-colors p-1 rounded-md hover:bg-base-300'
        >
          <XClose className='size-4' />
        </button>
      </div>
    );
  }, TOAST_OPTIONS);
}
